import { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackPageView } from './lib/analytics';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/auth-store';
import { useFeatureFlags } from './lib/feature-flags';
import { lazyWithReload } from './lib/lazyWithReload';
import { captureAndPersistUTM } from './lib/utm';
import { registerDeepLinkHandler } from './lib/native/deep-links';
import { ToastProvider } from './components/ui/Toast';
import { ErrorBoundary, NotFound } from './components/ErrorBoundary';
import {
  RequireAuth,
  RequireRole,
  RedirectIfAuthenticated,
} from './components/AuthGuard';
import {
  LearnerLayout,
  CreatorLayout,
  AdminLayout,
  OrgLayout,
  AuthLayout,
} from './components/layout/Layout';

// ============================================
// Lazy-loaded pages (route-based code splitting)
// ============================================

// Auth Pages
const Login = lazyWithReload(() => import('./pages/auth/Login'));
const Signup = lazyWithReload(() => import('./pages/auth/Signup'));
const ForgotPassword = lazyWithReload(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazyWithReload(() => import('./pages/auth/ResetPassword'));
const AuthCallback = lazyWithReload(() => import('./pages/auth/AuthCallback'));

// Learner App Pages
const Dashboard = lazyWithReload(() => import('./pages/app/Dashboard'));
const Practice = lazyWithReload(() => import('./pages/app/Practice'));
const VoicePractice = lazyWithReload(() => import('./pages/app/VoicePractice'));
const PracticeCategories = lazyWithReload(() => import('./pages/app/PracticeCategories'));
const Leaderboard = lazyWithReload(() => import('./pages/app/Leaderboard'));
const Profile = lazyWithReload(() => import('./pages/app/Profile'));
const Settings = lazyWithReload(() => import('./pages/app/Settings'));
const StudyGroups = lazyWithReload(() => import('./pages/app/StudyGroups'));
const Community = lazyWithReload(() => import('./pages/app/Community'));
const Forums = lazyWithReload(() => import('./pages/app/Forums'));
const Mentorship = lazyWithReload(() => import('./pages/app/Mentorship'));
const Referrals = lazyWithReload(() => import('./pages/app/Referrals'));
const Achievements = lazyWithReload(() => import('./pages/app/Achievements'));
const PublicProfile = lazyWithReload(() => import('./pages/app/PublicProfile'));
const Review = lazyWithReload(() => import('./pages/app/Review'));
const WeakAreas = lazyWithReload(() => import('./pages/app/WeakAreas'));
const InterviewPrep = lazyWithReload(() => import('./pages/app/InterviewPrep'));
const StreakHistory = lazyWithReload(() => import('./pages/app/StreakHistory'));
const CategoryAnalytics = lazyWithReload(() => import('./pages/app/CategoryAnalytics'));
const CMECredits = lazyWithReload(() => import('./pages/app/CMECredits'));
const SuccessStories = lazyWithReload(() => import('./pages/app/SuccessStories'));
const StudyGroupDetail = lazyWithReload(() => import('./pages/app/StudyGroupDetail'));
const ForumPostDetail = lazyWithReload(() => import('./pages/app/ForumPostDetail'));

// Creator Portal Pages
const CreatorDashboard = lazyWithReload(() => import('./pages/creator/CreatorDashboard'));
const SubmitQuestion = lazyWithReload(() => import('./pages/creator/SubmitQuestion'));
const MyQuestions = lazyWithReload(() => import('./pages/creator/MyQuestions'));
const PeerReview = lazyWithReload(() => import('./pages/creator/PeerReview'));
const EarningsDashboard = lazyWithReload(() => import('./pages/creator/EarningsDashboard'));
const CreatorMentorship = lazyWithReload(() => import('./pages/creator/CreatorMentorship'));
const QuestionAnalytics = lazyWithReload(() => import('./pages/creator/QuestionAnalytics'));
const CreatorSettings = lazyWithReload(() => import('./pages/creator/CreatorSettings'));
const RequestCreatorAccess = lazyWithReload(() => import('./pages/creator/RequestCreatorAccess'));

// Admin Portal Pages
const AdminDashboard = lazyWithReload(() => import('./pages/admin/AdminDashboard'));
const UsersManagement = lazyWithReload(() => import('./pages/admin/UsersManagement'));
const QuestionReports = lazyWithReload(() => import('./pages/admin/QuestionReports'));
const QuestionsManagement = lazyWithReload(() => import('./pages/admin/QuestionsManagement'));
const CreatorsManagement = lazyWithReload(() => import('./pages/admin/CreatorsManagement'));
const OrganizationsManagement = lazyWithReload(() => import('./pages/admin/OrganizationsManagement'));
const AdminAnalytics = lazyWithReload(() => import('./pages/admin/AdminAnalytics'));
const PaymentsManagement = lazyWithReload(() => import('./pages/admin/PaymentsManagement'));
const FeatureFlagsManagement = lazyWithReload(() => import('./pages/admin/FeatureFlagsManagement'));
const ContentImport = lazyWithReload(() => import('./pages/admin/ContentImport'));
const CreatorInvitations = lazyWithReload(() => import('./pages/admin/CreatorInvitations'));
const BlogManagement = lazyWithReload(() => import('./pages/admin/BlogManagement'));
const BlogEditor = lazyWithReload(() => import('./pages/admin/BlogEditor'));
const BlogPost = lazyWithReload(() => import('./pages/legal/BlogPost'));
const TrialUsers = lazyWithReload(() => import('./pages/admin/TrialUsers'));
const AdminReferrals = lazyWithReload(() => import('./pages/admin/AdminReferrals'));
const GrowthMetrics = lazyWithReload(() => import('./pages/admin/GrowthMetrics'));

// Public — claim-your-referral landing
const ClaimReferral = lazyWithReload(() => import('./pages/ClaimReferral'));

// B2B Portal Pages
const OrgDashboard = lazyWithReload(() => import('./pages/org/OrgDashboard'));
const ResidentManagement = lazyWithReload(() => import('./pages/org/ResidentManagement'));
const ChallengesManagement = lazyWithReload(() => import('./pages/org/ChallengesManagement'));
const OrgAnalytics = lazyWithReload(() => import('./pages/org/OrgAnalytics'));
const OrgBilling = lazyWithReload(() => import('./pages/org/OrgBilling'));
const OrgLeaderboard = lazyWithReload(() => import('./pages/org/OrgLeaderboard'));
const OrgSSOConfig = lazyWithReload(() => import('./pages/org/OrgSSOConfig'));

// Mentorship Pages
const MentorOnboarding = lazyWithReload(() => import('./pages/mentorship/MentorOnboarding'));

// Landing Page
const Landing = lazyWithReload(() => import('./pages/Landing'));

// Legal Pages
const Privacy = lazyWithReload(() => import('./pages/legal/Privacy'));
const Terms = lazyWithReload(() => import('./pages/legal/Terms'));
const Contact = lazyWithReload(() => import('./pages/legal/Contact'));
const About = lazyWithReload(() => import('./pages/legal/About'));
const Blog = lazyWithReload(() => import('./pages/legal/Blog'));
const Careers = lazyWithReload(() => import('./pages/legal/Careers'));

// Ad Landing Pages (assessment quiz funnels)
const GoogleSearchLanding = lazyWithReload(() => import('./pages/landing/GoogleSearchLanding'));
const SocialMediaLanding = lazyWithReload(() => import('./pages/landing/SocialMediaLanding'));
const B2BLanding = lazyWithReload(() => import('./pages/landing/B2BLanding'));
const ReferralLanding = lazyWithReload(() => import('./pages/landing/ReferralLanding'));

// SEO Marketing Pages
const CompetitorComparison = lazyWithReload(() => import('./pages/legal/CompetitorComparison'));
const CategoryBoardPrep = lazyWithReload(() => import('./pages/legal/CategoryBoardPrep'));
const AbfmExamGuide = lazyWithReload(() => import('./pages/legal/AbfmExamGuide'));
const IteStudyPlan = lazyWithReload(() => import('./pages/legal/IteStudyPlan'));
const TopicQuestions = lazyWithReload(() => import('./pages/legal/TopicQuestions'));
const ClinicalUpdatesPublic = lazyWithReload(() => import('./pages/legal/ClinicalUpdatesPublic'));
const ClinicalUpdates = lazyWithReload(() => import('./pages/app/ClinicalUpdates'));
const ClinicalUpdatesManagement = lazyWithReload(() => import('./pages/admin/ClinicalUpdatesManagement'));
const EmbedForPrograms = lazyWithReload(() => import('./pages/legal/EmbedForPrograms'));
const BoardReadinessEmbed = lazyWithReload(() => import('./pages/embed/BoardReadinessEmbed'));
const NewsletterConfirm = lazyWithReload(() => import('./pages/legal/NewsletterConfirm'));
const NewsletterUnsubscribe = lazyWithReload(() => import('./pages/legal/NewsletterUnsubscribe'));

// ============================================
// Loading fallback for lazy routes
// ============================================

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-charcoal-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount: number, error: Error) => {
        // Don't retry on 4xx errors
        if (error.message?.includes('4')) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Placeholder component for unimplemented pages
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="py-12 text-center">
      <h1 className="text-2xl font-bold text-charcoal-600 mb-2">{title}</h1>
      <p className="text-charcoal-400">This page is under construction.</p>
    </div>
  );
}

// SPA page-view tracking: gtag config uses send_page_view: false, so every
// view (including the initial one) is reported from here on route changes.
function PageViewBridge() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// SPA navigation keeps the previous scroll position, so a footer link lands
// the visitor at the bottom of the next page. Reset to top on pathname
// change; leave in-page anchors (#pricing) to the browser.
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
}

function DeepLinkBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    return registerDeepLinkHandler((path) => {
      navigate(path);
    });
  }, [navigate]);
  return null;
}

function App() {
  const { initialize } = useAuthStore();
  const { loadFlags } = useFeatureFlags();

  // Initialize auth and feature flags on app load
  useEffect(() => {
    // Capture ad/UTM attribution from the entry URL BEFORE auth init, so it's
    // in sessionStorage before any profile is created (getAcquisitionFields).
    // App-level so every entry point counts — the homepage and blog don't have
    // their own capture, and that's where ads usually land. First-touch:
    // persistUTM only overwrites when the current URL actually carries params.
    captureAndPersistUTM();
    initialize();
    loadFlags();
  }, [initialize, loadFlags]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <DeepLinkBridge />
            <PageViewBridge />
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/u/:username" element={<PublicProfile />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/careers" element={<Careers />} />

                {/* Ad Landing Pages — Assessment Quiz Funnels */}
                <Route path="/start/board-prep" element={<GoogleSearchLanding />} />
                <Route path="/start/residents" element={<SocialMediaLanding />} />
                <Route path="/start/programs" element={<B2BLanding />} />
                <Route path="/start/invite" element={<ReferralLanding />} />

                {/* SEO Marketing Pages */}
                <Route path="/vs/:slug" element={<CompetitorComparison />} />
                <Route path="/board-prep/:category" element={<CategoryBoardPrep />} />
                <Route path="/clinical-updates" element={<ClinicalUpdatesPublic />} />
                <Route path="/abfm-exam" element={<AbfmExamGuide />} />
                <Route path="/abfm-exam/:page" element={<AbfmExamGuide />} />
                <Route path="/tools/ite-study-plan" element={<IteStudyPlan />} />
                <Route path="/questions" element={<TopicQuestions />} />
                <Route path="/questions/:topic" element={<TopicQuestions />} />
                <Route path="/for-programs/embed" element={<EmbedForPrograms />} />
                <Route path="/embed/board-readiness" element={<BoardReadinessEmbed />} />

                {/* Newsletter management */}
                <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
                <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />

                {/* Claim-your-referral (B2B referrer lifetime Pro) */}
                <Route path="/claim" element={<ClaimReferral />} />

                {/* Auth Routes - Redirect if already authenticated */}
                <Route element={<AuthLayout />}>
                  <Route
                    path="/auth/login"
                    element={
                      <RedirectIfAuthenticated>
                        <Login />
                      </RedirectIfAuthenticated>
                    }
                  />
                  <Route
                    path="/auth/signup"
                    element={
                      <RedirectIfAuthenticated>
                        <Signup />
                      </RedirectIfAuthenticated>
                    }
                  />
                  <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                </Route>

                {/* OAuth redirect landing — must NOT be wrapped in
                    RedirectIfAuthenticated; it needs to process the callback
                    session itself, then routes the user to /app. */}
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Also support root-level auth routes for backwards compatibility */}
                <Route path="/login" element={<Navigate to="/auth/login" replace />} />
                <Route path="/signup" element={<Navigate to="/auth/signup" replace />} />
                <Route path="/forgot-password" element={<Navigate to="/auth/forgot-password" replace />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Learner App Routes */}
                <Route
                  element={
                    <RequireAuth>
                      <LearnerLayout />
                    </RequireAuth>
                  }
                >
                  <Route path="/app" element={<Dashboard />} />
                  <Route path="/app/practice" element={<Practice />} />
                  <Route path="/app/practice/voice" element={<VoicePractice />} />
                  <Route path="/app/practice/categories" element={<PracticeCategories />} />
                  <Route path="/app/leaderboard" element={<Leaderboard />} />
                  <Route path="/app/updates" element={<ClinicalUpdates />} />
                  <Route path="/app/community" element={<Community />} />
                  <Route path="/app/groups" element={<StudyGroups />} />
                  <Route path="/app/groups/:id" element={<StudyGroupDetail />} />
                  <Route path="/app/forums" element={<Forums />} />
                  <Route path="/app/forums/:id" element={<ForumPostDetail />} />
                  <Route path="/app/mentorship" element={<Mentorship />} />
                  <Route path="/app/referrals" element={<Referrals />} />
                  <Route path="/app/profile" element={<Profile />} />
                  <Route path="/app/settings" element={<Settings />} />
                  <Route path="/app/achievements" element={<Achievements />} />
                  <Route path="/app/review" element={<Review />} />
                  <Route path="/app/weak-areas" element={<WeakAreas />} />
                  <Route path="/app/interview-prep" element={<InterviewPrep />} />
                  <Route path="/app/streak-history" element={<StreakHistory />} />
                  <Route path="/app/categories" element={<CategoryAnalytics />} />
                  <Route path="/app/categories/:category" element={<CategoryAnalytics />} />
                  <Route path="/app/cme" element={<CMECredits />} />
                  <Route path="/app/success-stories" element={<SuccessStories />} />
                  <Route path="/app/become-mentor" element={<MentorOnboarding />} />
                  <Route path="/app/become-creator" element={<RequestCreatorAccess />} />
                </Route>

                {/* Creator Portal Routes - Requires creator or admin role */}
                <Route
                  element={
                    <RequireAuth>
                      <RequireRole allowedRoles={['creator', 'admin']}>
                        <CreatorLayout />
                      </RequireRole>
                    </RequireAuth>
                  }
                >
                  <Route path="/creator" element={<CreatorDashboard />} />
                  <Route path="/creator/submit" element={<SubmitQuestion />} />
                  <Route path="/creator/questions" element={<MyQuestions />} />
                  <Route path="/creator/edit/:id" element={<SubmitQuestion />} />
                  <Route path="/creator/reviews" element={<PeerReview />} />
                  <Route path="/creator/analytics/:id" element={<QuestionAnalytics />} />
                  <Route path="/creator/earnings" element={<EarningsDashboard />} />
                  <Route path="/creator/mentorship" element={<CreatorMentorship />} />
                  <Route path="/creator/settings" element={<CreatorSettings />} />
                </Route>

                {/* Admin Portal Routes - Requires admin role */}
                <Route
                  element={
                    <RequireAuth>
                      <RequireRole allowedRoles={['admin']}>
                        <AdminLayout />
                      </RequireRole>
                    </RequireAuth>
                  }
                >
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/questions" element={<QuestionsManagement />} />
                  <Route path="/admin/question-reports" element={<QuestionReports />} />
                  <Route path="/admin/users" element={<UsersManagement />} />
                  <Route path="/admin/creators" element={<CreatorsManagement />} />
                  <Route path="/admin/organizations" element={<OrganizationsManagement />} />
                  <Route path="/admin/analytics" element={<AdminAnalytics />} />
                  <Route path="/admin/payments" element={<PaymentsManagement />} />
                  <Route path="/admin/flags" element={<FeatureFlagsManagement />} />
                  <Route path="/admin/content-import" element={<ContentImport />} />
                  <Route path="/admin/creator-invitations" element={<CreatorInvitations />} />
                  <Route path="/admin/blog" element={<BlogManagement />} />
                  <Route path="/admin/clinical-updates" element={<ClinicalUpdatesManagement />} />
                  <Route path="/admin/blog/new" element={<BlogEditor />} />
                  <Route path="/admin/blog/edit/:id" element={<BlogEditor />} />
                  <Route path="/admin/trial-users" element={<TrialUsers />} />
                  <Route path="/admin/referrals" element={<AdminReferrals />} />
                  <Route path="/admin/growth-metrics" element={<GrowthMetrics />} />
                  <Route path="/admin/settings" element={<PlaceholderPage title="Admin Settings" />} />
                </Route>

                {/* B2B Portal Routes - Requires org_admin or admin role */}
                <Route
                  element={
                    <RequireAuth>
                      <RequireRole allowedRoles={['org_admin', 'admin']}>
                        <OrgLayout />
                      </RequireRole>
                    </RequireAuth>
                  }
                >
                  <Route path="/org" element={<OrgDashboard />} />
                  <Route path="/org/residents" element={<ResidentManagement />} />
                  <Route path="/org/analytics" element={<OrgAnalytics />} />
                  <Route path="/org/challenges" element={<ChallengesManagement />} />
                  <Route path="/org/leaderboard" element={<OrgLeaderboard />} />
                  <Route path="/org/billing" element={<OrgBilling />} />
                  <Route path="/org/sso" element={<OrgSSOConfig />} />
                  <Route path="/org/settings" element={<PlaceholderPage title="Organization Settings" />} />
                </Route>

                {/* Catch all - 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
